/*
PROV: COHINT001.BF.1UUSSIYBMG
REQ: SYS-ARCH-15
WHY: Shared package module.
*/

export type LogLevel = "debug" | "info" | "warn" | "error";

const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const OTP_6_RE = /\b\d{6}\b/g;

const SENSITIVE_LOG_KEYS = new Set([
  "authorization",
  "x-admin-token",
  "x-support-token",
  "x-superuser-token",
  "cookie",
  "set-cookie",
]);

const PII_EMAIL_KEYS = new Set(["email", "to_email", "from_email", "support_contact_email"]);
const PII_OTP_KEYS = new Set(["otp", "otp_code", "totp", "totp_code", "passcode", "one_time_passcode", "one_time_password"]);

function redactString(value: string): string {
  const lowered = value.toLowerCase();
  for (const key of SENSITIVE_LOG_KEYS) {
    if (lowered.includes(key)) return "[REDACTED]";
  }
  let redacted = value.replace(EMAIL_RE, "[REDACTED_EMAIL]");
  if (/(otp|passcode|one-time|one time|verification code)/.test(lowered)) {
    redacted = redacted.replace(OTP_6_RE, "[REDACTED_OTP]");
  }
  return redacted;
}

function redactValue(value: unknown): unknown {
  if (typeof value === "string") return redactString(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const kl = k.toLowerCase();
      if (SENSITIVE_LOG_KEYS.has(kl)) out[k] = "[REDACTED]";
      else if (PII_EMAIL_KEYS.has(kl)) out[k] = "[REDACTED_EMAIL]";
      else if (PII_OTP_KEYS.has(kl)) out[k] = "[REDACTED_OTP]";
      else out[k] = redactValue(v);
    }
    return out;
  }
  return value;
}

export type LogFields = Record<string, unknown>;

export type Logger = {
  debug: (event: string, fields?: LogFields) => void;
  info: (event: string, fields?: LogFields) => void;
  warn: (event: string, fields?: LogFields) => void;
  error: (event: string, fields?: LogFields) => void;
};

export function createLogger(opts?: {
  service?: string;
  appEnv?: string;
  appVersion?: string;
  schema?: string;
}): Logger {
  const service = opts?.service ?? (process.env.COHORTA_SERVICE_NAME || "unknown");
  const appEnv = opts?.appEnv ?? (process.env.APP_ENV || "local");
  const appVersion = opts?.appVersion ?? (process.env.COHORTA_APP_VERSION || process.env.GIT_SHA || "unknown");
  const schema = opts?.schema ?? "cohorta.log.v1";

  function emit(level: LogLevel, event: string, fields?: LogFields) {
    const payload: Record<string, unknown> = {
      schema,
      ts: new Date().toISOString(),
      level,
      event,
      msg: event,
      service,
      app_env: String(appEnv).toLowerCase(),
      app_version: appVersion,
      ...(fields || {}),
    };

    const safe = redactValue(payload) as Record<string, unknown>;

    // Use console.* as the transport; schema makes it machine-parseable.
    if (level === "error") console.error(JSON.stringify(safe));
    else if (level === "warn") console.warn(JSON.stringify(safe));
    else console.log(JSON.stringify(safe));
  }

  return {
    debug: (event, fields) => emit("debug", event, fields),
    info: (event, fields) => emit("info", event, fields),
    warn: (event, fields) => emit("warn", event, fields),
    error: (event, fields) => emit("error", event, fields),
  };
}
